import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  Copy,
  DoorOpen,
  ExternalLink,
  Globe,
  Link2,
  MessageCircle,
  Scissors,
  ShieldCheck,
  Smartphone,
  Sparkles,
  UserCheck,
  Users,
  Wrench,
} from "lucide-react";
import { useLocation as useLocationContext } from "@/contexts/LocationContext";
import { useGlobalToast } from "@/contexts/ToastContext";
import { useSetupWizard } from "@/contexts/SetupWizardContext";

/**
 * Guía rápida para negocios de CITAS (peluquerías, clínicas, estética…):
 * qué hay que crear, en qué orden y desde qué pantalla, para que el
 * negocio reciba citas online. El orden y lo "imprescindible" salen del
 * motor real de disponibilidad: sin servicio activo el widget está vacío;
 * sin profesional con horario y servicio asignado no hay ni un hueco; el
 * horario del centro acota el de los profesionales.
 */

const WIDGET_BASE_URL =
  import.meta.env.VITE_WIDGET_URL || "https://widget.reservamanager.com";

type Nivel = "imprescindible" | "recomendado" | "opcional";

const NIVEL_STYLE: Record<Nivel, { label: string; className: string }> = {
  imprescindible: { label: "Imprescindible", className: "bg-red-50 text-red-700 border-red-200" },
  recomendado: { label: "Recomendado", className: "bg-amber-50 text-amber-800 border-amber-200" },
  opcional: { label: "Opcional", className: "bg-gray-50 text-gray-600 border-gray-200" },
};

interface Paso {
  numero: number;
  nivel: Nivel;
  icon: ReactNode;
  titulo: string;
  donde: string;
  ruta: string;
  cta: string;
  resumen: string;
  puntos: string[];
  ojo?: string;
}

const PASOS_IMPRESCINDIBLES: Paso[] = [
  {
    numero: 1,
    nivel: "imprescindible",
    icon: <CalendarClock className="w-5 h-5" />,
    titulo: "Revisa el horario del centro",
    donde: "Mi Local › Horarios",
    ruta: "/appointments/settings",
    cta: "Ir a Horarios",
    resumen:
      "Es el marco de todo: tus profesionales solo pueden dar citas dentro de este horario.",
    puntos: [
      "Viene de fábrica de lunes a viernes, de 9:00 a 14:00 y de 16:00 a 20:00, con sábado y domingo cerrados. Si abres el sábado, márcalo aquí o nadie podrá pedir cita ese día.",
      "En «Días especiales y festivos» cierras un día concreto o le pones un horario distinto (vacaciones, puentes, formación).",
      "En «Reglas de reserva» decides si el cliente elige profesional, con cuánta antelación mínima puede reservar y hasta cuántos días vista.",
    ],
    ojo: "Guarda los cambios con el botón de arriba a la derecha; si no, no se aplican.",
  },
  {
    numero: 2,
    nivel: "imprescindible",
    icon: <Scissors className="w-5 h-5" />,
    titulo: "Da de alta tus servicios",
    donde: "Mi Local › Servicios",
    ruta: "/services",
    cta: "Crear servicios",
    resumen: "Sin al menos un servicio activo, tu enlace de citas sale vacío.",
    puntos: [
      "Cada servicio necesita nombre y duración en minutos (obligatoria): es lo que reserva el hueco en la agenda. El precio es opcional y solo se muestra.",
      "Ponle el nombre que usaría tu cliente («Corte de pelo», «Limpieza facial», «Primera consulta»), no jerga interna.",
      "Deja marcado «Servicio activo». Un servicio desactivado desaparece del enlace pero no se borra.",
    ],
  },
  {
    numero: 3,
    nivel: "imprescindible",
    icon: <UserCheck className="w-5 h-5" />,
    titulo: "Añade a tus profesionales",
    donde: "Mi Local › Profesionales",
    ruta: "/specialists",
    cta: "Añadir profesionales",
    resumen:
      "Las citas se dan a una persona. Sin profesional con horario y servicios no hay ni un hueco libre.",
    puntos: [
      "Si trabajas solo o sola, date de alta a ti mismo como profesional: es normal y necesario.",
      "El alta tiene 3 pasos: datos (nombre y ubicación), su horario semanal (viene propuesto de lunes a sábado, 9–13 y 16–20; ajústalo) y los servicios que realiza (al menos uno).",
      "Después, desde el botón «Horario» de cada profesional gestionas sus vacaciones, días libres y bloqueos puntuales (una reunión, una comida).",
    ],
    ojo: "Comprueba que está en la ubicación correcta y activo. Un profesional de otra sede, sin servicios o desactivado nunca aparece disponible.",
  },
  {
    numero: 4,
    nivel: "imprescindible",
    icon: <Link2 className="w-5 h-5" />,
    titulo: "Comparte tu enlace de citas",
    donde: "Mi negocio › Integración",
    ruta: "/integration",
    cta: "Conseguir mi enlace",
    resumen:
      "Es la puerta por la que entran las citas: ponlo donde te busquen tus clientes.",
    puntos: [
      "Copia el enlace y pégalo en el perfil de Instagram, en el botón «Reservar» de tu ficha de Google y en Facebook. En «Tu Web» tienes el código para incrustarlo en tu página.",
      "Lo que verá tu cliente: elige servicio → profesional (o «cualquiera») → día → hora → deja nombre, teléfono y email. Recibe un email de confirmación con el que puede confirmar, cancelar o pedir un cambio.",
      "Cada cita nueva te llega al panel y a la campanita; también puedes crearlas tú a mano desde «Citas».",
    ],
  },
];

const PASOS_RECOMENDADOS: Paso[] = [
  {
    numero: 5,
    nivel: "recomendado",
    icon: <Building2 className="w-5 h-5" />,
    titulo: "Completa la ficha de tu negocio",
    donde: "Mi negocio › Ficha del negocio",
    ruta: "/locations",
    cta: "Completar ficha",
    resumen:
      "Dirección, ciudad, teléfono, logo y foto de portada: salen en tu enlace de citas, en tu página web y en la app.",
    puntos: [
      "Al registrarte la dirección queda «Por configurar». Ponla real y pega el enlace de Google Maps: de ahí sacamos tu posición en el mapa.",
      "El teléfono y el logo aparecen en la cabecera de tu enlace de citas; la foto de portada, en la app y en tu página.",
    ],
  },
  {
    numero: 6,
    nivel: "recomendado",
    icon: <ShieldCheck className="w-5 h-5" />,
    titulo: "Ajusta las reglas de cita",
    donde: "Mi Local › Horarios (más abajo)",
    ruta: "/appointments/settings",
    cta: "Ver reglas",
    resumen: "Decide cómo quieres trabajar; los valores de fábrica funcionan bien para empezar.",
    puntos: [
      "«Confirmar citas automáticamente» viene activado: la cita entra confirmada. Apágalo si prefieres aprobarlas tú una a una.",
      "Antelación mínima (2 h de fábrica) y máxima (60 días); cancelación online y con cuántas horas de margen (24 h); si para cambiar una cita te llaman o te escriben por WhatsApp.",
      "Recordatorios: el de email va activado 24 h antes. El de WhatsApp es opcional y necesita un plan con WhatsApp.",
    ],
  },
  {
    numero: 7,
    nivel: "opcional",
    icon: <DoorOpen className="w-5 h-5" />,
    titulo: "Salas o cabinas",
    donde: "Mi Local › Salas",
    ruta: "/appointment-rooms",
    cta: "Ver salas",
    resumen: "Solo si necesitas saber en qué sala o cabina se hace cada cita.",
    puntos: [
      "Crea la sala, marca qué servicios se hacen en ella y qué profesionales la usan. Se asigna sola al entrar la cita; si no hay sala, la cita se crea igual.",
    ],
  },
];

const EXTRAS: Array<{ icon: ReactNode; titulo: string; ruta: string; texto: string }> = [
  {
    icon: <Globe className="w-5 h-5" />,
    titulo: "Tu página web, incluida en el plan",
    ruta: "/public-profile",
    texto:
      "Una página pública con tus fotos, tu horario y el botón «Pedir cita». Pide descripción, al menos una foto, teléfono y horario. La primera vez la revisamos nosotros; después los cambios salen al momento.",
  },
  {
    icon: <Smartphone className="w-5 h-5" />,
    titulo: "Aparecer en la app de ReservaManager",
    ruta: "/locations",
    texto:
      "Interruptor «Visible en la app» en Ficha del negocio. Necesita la ficha completa (dirección, ciudad y mapa) y una suscripción o prueba en vigor.",
  },
  {
    icon: <Users className="w-5 h-5" />,
    titulo: "Tu equipo en el panel",
    ruta: "/team",
    texto:
      "Invita a tus compañeros y cada uno verá las citas del día desde su móvil. Depende de los usuarios que admita tu plan.",
  },
  {
    icon: <CalendarDays className="w-5 h-5" />,
    titulo: "Google Calendar de cada profesional",
    ruta: "/specialists",
    texto:
      "Botón «Conectar» en la ficha del profesional: le mandas un enlace, acepta con su cuenta de Google y sus citas aparecen en un calendario propio. Lo que él bloquee ahí, deja de ofrecerse.",
  },
  {
    icon: <MessageCircle className="w-5 h-5" />,
    titulo: "WhatsApp de confirmación y recordatorio",
    ruta: "/notifications",
    texto:
      "Con un plan que incluya WhatsApp, el cliente recibe la confirmación y el recordatorio de su cita por WhatsApp. Se activa en Notificaciones.",
  },
  {
    icon: <Sparkles className="w-5 h-5" />,
    titulo: "Carmen, tu recepcionista con IA",
    ruta: "/subscription",
    texto:
      "Add-ons de pago: Carmen atiende el WhatsApp del negocio o el teléfono y da, cambia y cancela citas sola, eligiendo servicio y profesional. Se contratan en Suscripción.",
  },
];

function NivelChip({ nivel }: { nivel: Nivel }) {
  const s = NIVEL_STYLE[nivel];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold uppercase tracking-wide ${s.className}`}>
      {s.label}
    </span>
  );
}

function PasoCard({ paso }: { paso: Paso }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 md:p-6">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold">
          {paso.numero}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span className="text-primary-600">{paso.icon}</span>
              {paso.titulo}
            </h3>
            <NivelChip nivel={paso.nivel} />
          </div>
          <p className="text-sm text-gray-500 mt-0.5">
            Dónde: <span className="font-medium text-gray-700">{paso.donde}</span>
          </p>
          <p className="text-gray-700 mt-3">{paso.resumen}</p>
          <ul className="mt-3 space-y-2">
            {paso.puntos.map((punto, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>{punto}</span>
              </li>
            ))}
          </ul>
          {paso.ojo && (
            <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <span className="font-semibold">Ojo:</span> {paso.ojo}
            </p>
          )}
          <Link
            to={paso.ruta}
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
          >
            {paso.cta} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function AppointmentsGuide() {
  const { currentLocation, currentBookingMode } = useLocationContext();
  const { showToast } = useGlobalToast();
  const { abrir: abrirAsistente } = useSetupWizard();
  const widgetUrl = currentLocation?.slug ? `${WIDGET_BASE_URL}/${currentLocation.slug}` : null;

  if (currentBookingMode !== "appointments") {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-xl border border-gray-200 p-6 text-center">
        <p className="text-gray-700">Esta guía es para negocios que trabajan con citas.</p>
        <Link to="/" className="inline-flex items-center gap-2 mt-4 text-primary-600 font-medium">
          Volver al panel <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  const copiarEnlace = async () => {
    if (!widgetUrl) return;
    try {
      await navigator.clipboard.writeText(widgetUrl);
      showToast("Enlace copiado", "success");
    } catch {
      showToast("No se pudo copiar. Cópialo a mano desde Integración.", "error");
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-12">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Guía rápida: tu negocio de citas en marcha</h1>
        <p className="text-gray-600 mt-2">
          En cuatro pasos tus clientes pueden pedir cita online, a cualquier hora, sin llamarte. Los cuatro
          primeros son imprescindibles; el resto mejora el resultado y lo puedes hacer más adelante.
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          <NivelChip nivel="imprescindible" />
          <NivelChip nivel="recomendado" />
          <NivelChip nivel="opcional" />
        </div>

        {/* Esta guía EXPLICA; el asistente guiado lo RELLENA (27-sep-2026).
            Quien no quiera leerse los pasos puede contestar cuatro preguntas y
            terminar sin recorrer las pantallas. */}
        <button
          type="button"
          onClick={() => abrirAsistente()}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700"
        >
          <Sparkles className="w-4 h-4" />
          Prefiero que me lo preguntes paso a paso
        </button>
      </div>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Para recibir citas necesitas esto</h2>
        {PASOS_IMPRESCINDIBLES.map((paso) => (
          <PasoCard key={paso.numero} paso={paso} />
        ))}
      </section>

      <section className="bg-primary-50 border border-primary-200 rounded-xl p-5 md:p-6">
        <h2 className="text-lg font-semibold text-primary-900 flex items-center gap-2">
          <Wrench className="w-5 h-5" /> Comprueba que funciona
        </h2>
        <ol className="mt-3 space-y-2 text-sm text-primary-900 list-decimal list-inside">
          <li>Abre tu enlace de citas como si fueras un cliente y pide una cita de prueba.</li>
          <li>
            Mírala en <Link to="/appointments" className="underline font-medium">Citas</Link> y cancélala desde ahí.
          </li>
        </ol>
        {widgetUrl && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2">
            <code className="flex-1 min-w-0 truncate bg-white border border-primary-200 rounded-lg px-3 py-2 text-sm text-gray-800">
              {widgetUrl}
            </code>
            <div className="flex gap-2">
              <button
                onClick={() => void copiarEnlace()}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-white border border-primary-200 text-primary-700 rounded-lg hover:bg-primary-100"
              >
                <Copy className="w-4 h-4" /> Copiar
              </button>
              <a
                href={widgetUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <ExternalLink className="w-4 h-4" /> Abrir
              </a>
            </div>
          </div>
        )}
        <div className="mt-4 text-sm text-primary-900 space-y-1.5">
          <p className="font-semibold">Si algo no sale:</p>
          <p>
            «No hay servicios disponibles» → te falta un servicio activo (paso 2).
          </p>
          <p>
            «No hay horarios disponibles este día» → ese día el centro está cerrado (paso 1), el profesional no trabaja o
            no tiene ese servicio asignado (paso 3), o es demasiado pronto para la antelación mínima (paso 6).
          </p>
          <p>El enlace da error → tu suscripción o tu periodo de prueba ha terminado: míralo en Suscripción.</p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Para que quede bien</h2>
        {PASOS_RECOMENDADOS.map((paso) => (
          <PasoCard key={paso.numero} paso={paso} />
        ))}
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Cuando quieras ir más allá</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {EXTRAS.map((extra) => (
            <Link
              key={extra.titulo}
              to={extra.ruta}
              className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow flex items-start gap-3"
            >
              <span className="text-primary-600 mt-0.5">{extra.icon}</span>
              <span>
                <span className="block font-semibold text-gray-900">{extra.titulo}</span>
                <span className="block text-sm text-gray-600 mt-1">{extra.texto}</span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      <p className="text-sm text-gray-500">
        ¿Te atascas en algún paso? En el{" "}
        <Link to="/help" className="text-primary-600 font-medium underline">
          Centro de Ayuda
        </Link>{" "}
        tienes el chat de soporte y el WhatsApp del equipo.
      </p>
    </div>
  );
}
